import { warmMessageBodies as warmMessageBodiesApi } from "@/lib/api/imap";
import {
  INBOX_BODY_WARMUP_MAX,
  INBOX_BODY_WARM_BATCH,
  INBOX_BODY_WARM_BATCH_GAP_MS,
  INBOX_BODY_WARM_DEFER_MS,
  INBOX_QUEUED_WARM_POLL_MAX_MS,
  INBOX_QUEUED_WARM_POLL_MS,
} from "@/lib/constants/inbox";
import { fetchBodyCacheStatusCoordinated } from "@/lib/mail/bodyCacheStatusCoordinator";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import { notifyBodiesWarmed } from "@/lib/mail/mailBodyWarmEvents";
import { clearBodiesWarming, markBodiesWarming } from "@/lib/mail/mailBodyWarmState";

type FolderKey = string;

const warmInflight = new Map<FolderKey, Promise<number>>();
const staggerGeneration = new Map<FolderKey, number>();
const queuedPollGeneration = new Map<FolderKey, number>();

function folderKey(accountId: number, folder: string): FolderKey {
  return `${accountId}\0${folder}`;
}

function normalizeWarmUids(uids: number[], max = INBOX_BODY_WARMUP_MAX): number[] {
  return [...new Set(uids.filter((uid) => uid > 0))].slice(0, max);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Worker async : poll DD02 jusqu’à prêt (sinon le bandeau « prépa » tombe en nuage idle).
 */
function startQueuedWarmStatusPoll(
  accountId: number,
  folder: string,
  uids: number[]
): void {
  const pending = normalizeWarmUids(uids);
  if (pending.length === 0) return;

  const key = folderKey(accountId, folder);
  const generation = (queuedPollGeneration.get(key) ?? 0) + 1;
  queuedPollGeneration.set(key, generation);
  const deadline = Date.now() + INBOX_QUEUED_WARM_POLL_MAX_MS;
  let remaining = [...pending];

  void (async () => {
    while (remaining.length > 0 && Date.now() < deadline) {
      if (queuedPollGeneration.get(key) !== generation) return;

      // Rafraîchir le timestamp warming (anti stale → nuage trop tôt).
      markBodiesWarming(accountId, folder, remaining);
      await delay(INBOX_QUEUED_WARM_POLL_MS);
      if (queuedPollGeneration.get(key) !== generation) return;

      const res = await fetchBodyCacheStatusCoordinated({
        accountId,
        folder,
        uids: remaining,
        force: true,
      });
      if (!res.ok) continue;

      const flags = res.data.flags ?? {};
      const readyUids = remaining.filter((uid) => flags[String(uid)] === true);
      if (readyUids.length > 0) {
        notifyBodiesWarmed(
          accountId,
          folder,
          Object.fromEntries(readyUids.map((uid) => [String(uid), true]))
        );
        clearBodiesWarming(accountId, folder, readyUids);
        remaining = remaining.filter((uid) => !readyUids.includes(uid));
      }
    }

    if (queuedPollGeneration.get(key) !== generation) return;
    if (remaining.length > 0) {
      // Timeout : nuage idle honnête (cliquable) plutôt que spinner éternel.
      clearBodiesWarming(accountId, folder, remaining);
    }
  })();
}

/**
 * Prépare les corps en DD02 (IMAP → cache serveur).
 */
export async function warmMessageBodies(
  accountId: number,
  folder: string,
  uids: number[],
  options?: { max?: number }
): Promise<number> {
  const max = options?.max ?? INBOX_BODY_WARMUP_MAX;
  const unique = normalizeWarmUids(uids, max);
  if (unique.length === 0) return 0;

  const key = folderKey(accountId, folder);

  const run = async (): Promise<number> => {
    markBodiesWarming(accountId, folder, unique);
    try {
      const res = await warmMessageBodiesApi({ accountId, folder, uids: unique });
      const isDraftsFolder = navFolderSlot(folder, "") === "drafts";
      if (res.ok) {
        const flags = res.data.flags ?? {};
        const queued = res.data.queued === true;

        if (isDraftsFolder) {
          notifyBodiesWarmed(
            accountId,
            folder,
            Object.fromEntries(unique.map((uid) => [String(uid), true]))
          );
          clearBodiesWarming(accountId, folder, unique);
          return res.data.warmed ?? 0;
        }

        notifyBodiesWarmed(accountId, folder, flags);
        const readyUids = Object.entries(flags)
          .filter(([, ready]) => ready === true)
          .map(([uid]) => Number(uid))
          .filter((uid) => uid > 0);
        clearBodiesWarming(accountId, folder, readyUids);

        // Queue worker : laisser warming + poll jusqu’à DD02 (évite réapparition en nuage).
        if (queued) {
          const pending = unique.filter((uid) => !readyUids.includes(uid));
          if (pending.length > 0) {
            startQueuedWarmStatusPoll(accountId, folder, pending);
          }
          return res.data.warmed ?? 0;
        }

        const pending = unique.filter((uid) => !readyUids.includes(uid));
        if (pending.length > 0) {
          clearBodiesWarming(accountId, folder, pending);
        }
        return res.data.warmed ?? 0;
      }
      if (isDraftsFolder) {
        notifyBodiesWarmed(
          accountId,
          folder,
          Object.fromEntries(unique.map((uid) => [String(uid), true]))
        );
        clearBodiesWarming(accountId, folder, unique);
      }
      clearBodiesWarming(accountId, folder, unique);
      return 0;
    } catch {
      clearBodiesWarming(accountId, folder, unique);
      return 0;
    }
  };

  const previous = warmInflight.get(key) ?? Promise.resolve(0);
  const promise = previous.then(run, run);
  warmInflight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (warmInflight.get(key) === promise) {
      warmInflight.delete(key);
    }
  }
}

/** Annule un warm échelonné planifié (changement de dossier). */
export function cancelStaggeredBodyWarm(accountId: number, folder: string): void {
  const key = folderKey(accountId, folder);
  staggerGeneration.set(key, (staggerGeneration.get(key) ?? 0) + 1);
  queuedPollGeneration.set(key, (queuedPollGeneration.get(key) ?? 0) + 1);
}

/**
 * Warm corps à délai optionnel.
 * Nouveaux mails : defer 0, 1 UID/vague, pastille immédiate.
 */
export function queueStaggeredBodyWarm(
  accountId: number,
  folder: string,
  uids: number[],
  options?: {
    deferMs?: number;
    batchSize?: number;
    batchGapMs?: number;
    newMail?: boolean;
  }
): void {
  const unique = normalizeWarmUids(uids);
  if (unique.length === 0) return;

  const key = folderKey(accountId, folder);
  const generation = (staggerGeneration.get(key) ?? 0) + 1;
  staggerGeneration.set(key, generation);

  const newMail = options?.newMail === true;
  const deferMs = options?.deferMs ?? (newMail ? 0 : INBOX_BODY_WARM_DEFER_MS);
  const batchSize = Math.max(1, options?.batchSize ?? (newMail ? 1 : INBOX_BODY_WARM_BATCH));
  const batchGapMs = options?.batchGapMs ?? (newMail ? 280 : INBOX_BODY_WARM_BATCH_GAP_MS);

  if (newMail && unique.length > 0) {
    markBodiesWarming(accountId, folder, [unique[0]]);
  }

  const run = async () => {
    for (let offset = 0; offset < unique.length; offset += batchSize) {
      if (staggerGeneration.get(key) !== generation) return;

      const batch = unique.slice(offset, offset + batchSize);
      await warmMessageBodies(accountId, folder, batch);

      const nextUid = unique[offset + batchSize];
      if (newMail && nextUid != null && staggerGeneration.get(key) === generation) {
        markBodiesWarming(accountId, folder, [nextUid]);
      }

      if (offset + batchSize < unique.length) {
        await delay(batchGapMs);
      }
    }
  };

  if (deferMs === 0) {
    void run();
    return;
  }

  window.setTimeout(() => {
    if (staggerGeneration.get(key) !== generation) return;
    void run();
  }, deferMs);
}

/** Nouveaux mails reçus — warm séquentiel immédiat. */
export function queueWarmMessageBodies(
  accountId: number,
  folder: string,
  uids: number[],
  options?: { newMail?: boolean }
): void {
  queueStaggeredBodyWarm(accountId, folder, uids, {
    newMail: options?.newMail === true,
  });
}
